import type { TradeLogicComparisonOperatorV2 } from '@/types/appRouter';
import {
    createLogicAction,
    createLogicCondition,
    type LogicActionNode,
    type LogicConditionNode,
    type TradeLogicDocumentKind,
    type TradeLogicComparisonOperator,
    type TradeLogicMissingDataPolicy,
    type TradeLogicNodeIdFactory
} from '../model';
import type {
    TradeLogicRegistryActionTemplateDto,
    TradeLogicRegistryConditionTemplateDto,
    TradeLogicRegistryConditionTemplateFeatureOptionDto,
    TradeLogicRegistryFeatureDto
} from '../registry';
import {
    buildConditionTemplateValueField,
    buildDefaultSchemaValues,
    buildFeatureFilters,
    buildParsedSchemaValues,
    getConditionTemplateFeatureRef,
    parseConditionValue,
    parseSchemaInputValue,
    schemaFieldValueToInputString,
    schemaValueToInputString
} from './schemaControlUtils';

export interface ConditionTemplateSelectionDefaults {
    featureOptionKey: string;
    operator: string;
    parameterValues: Record<string, string>;
    value: string;
}

export const buildConditionTemplateSelectionDefaults = (
    templateKey: string,
    templatesByKey: Record<string, TradeLogicRegistryConditionTemplateDto>
): ConditionTemplateSelectionDefaults => {
    const nextTemplate = templateKey ? (templatesByKey[templateKey] ?? null) : null;
    const nextFeatureOption = nextTemplate?.contextFeatureOptions[0] ?? null;
    const nextValueField = buildConditionTemplateValueField(nextTemplate);

    const value = nextValueField
        ? schemaFieldValueToInputString(nextValueField, nextValueField.defaultValue ?? nextFeatureOption?.defaultValue ?? null)
        : schemaValueToInputString(nextFeatureOption?.defaultValue ?? null);

    return {
        featureOptionKey: nextFeatureOption?.optionKey ?? '',
        operator: nextFeatureOption?.defaultOperator ?? nextTemplate?.allowedOperators[0] ?? '=',
        parameterValues: buildDefaultSchemaValues(nextTemplate?.parameterFields ?? []),
        value
    };
};

export const buildFeatureConditionNode = ({
    createId,
    feature,
    filterOperators,
    filterValues,
    missingDataPolicy,
    operator,
    selectorValues,
    value
}: {
    createId: TradeLogicNodeIdFactory;
    feature: TradeLogicRegistryFeatureDto | null;
    filterOperators: Record<string, TradeLogicComparisonOperatorV2>;
    filterValues: Record<string, string>;
    missingDataPolicy: '' | TradeLogicMissingDataPolicy;
    operator: string;
    selectorValues: Record<string, string>;
    value: string;
}): LogicConditionNode => {
    const blockingReason = getFeatureConditionBlockingReason(feature);

    if (blockingReason) throw new Error(blockingReason);

    if (!feature) throw new Error('Select a registry feature before adding a condition.');

    const selectors = buildParsedSchemaValues(feature.selectorFields, selectorValues);
    const filters = buildFeatureFilters(feature.filterFields, filterValues, filterOperators);
    const parsedValue = parseConditionValue(feature, operator, value);
    const parsedMissingDataPolicy = missingDataPolicy || null;

    return createLogicCondition(
        {
            label: feature.label,
            featureRef: {
                kind: 'contextFeature',
                featureKey: feature.featureKey,
                duration: feature.defaultDuration,
                selectors,
                filters: filters.length > 0 ? filters : null,
                missingDataPolicy: parsedMissingDataPolicy
            },
            operator: coerceComparisonOperator(operator),
            value: parsedValue,
            missingDataPolicy: parsedMissingDataPolicy
        },
        { createId }
    );
};

export const buildConditionTemplateNode = ({
    createId,
    featureOption,
    operator,
    parameterValues,
    template,
    value
}: {
    createId: TradeLogicNodeIdFactory;
    featureOption: TradeLogicRegistryConditionTemplateFeatureOptionDto | null;
    operator: string;
    parameterValues: Record<string, string>;
    template: TradeLogicRegistryConditionTemplateDto | null;
    value: string;
}): LogicConditionNode => {
    if (!template) {
        throw new Error('Select a condition template before adding a condition.');
    }

    const parameters = buildParsedSchemaValues(template.parameterFields, parameterValues);
    const featureRef = getConditionTemplateFeatureRef(featureOption);
    const valueField = buildConditionTemplateValueField(template);

    const parsedValue = valueField ? parseSchemaInputValue(value, valueField) : (featureOption?.defaultValue ?? null);

    return createLogicCondition(
        {
            label: template.label,
            templateKey: template.templateKey,
            requirementType: template.requirementType,
            featureRef,
            operator: coerceComparisonOperator(operator || featureOption?.defaultOperator || template.allowedOperators[0]),
            value: parsedValue,
            parameters
        },
        { createId }
    );
};

export const buildActionTemplateNode = ({
    createId,
    documentKind,
    parameterValues,
    template
}: {
    createId: TradeLogicNodeIdFactory;
    documentKind?: TradeLogicDocumentKind;
    parameterValues: Record<string, string>;
    template: TradeLogicRegistryActionTemplateDto | null;
}): LogicActionNode => {
    if (!template) {
        throw new Error('Select an action template before adding an action.');
    }

    if (documentKind && template.documentKinds.length > 0 && !template.documentKinds.includes(documentKind)) {
        throw new Error(`${template.label} cannot be added to ${documentKind} drafts.`);
    }

    return createLogicAction(
        {
            label: template.label,
            templateKey: template.templateKey,
            actionKind: template.actionKind ?? template.templateKey,
            parameters: buildParsedSchemaValues(template.parameterFields, parameterValues)
        },
        { createId }
    );
};

export const getFeatureConditionBlockingReason = (feature: TradeLogicRegistryFeatureDto | null): string | null => {
    if (!feature) return 'Select a registry feature before adding a condition.';
    if (!feature.capabilities.canAuthor) return feature.capabilities.unsupportedReason ?? `${feature.label} is not authorable.`;
    if (feature.allowedOperators.length === 0) return `${feature.label} does not expose authoring operators.`;

    return null;
};

const coerceComparisonOperator = (operator: string | null | undefined): TradeLogicComparisonOperator => {
    return (operator ?? '=') as TradeLogicComparisonOperator;
};
